import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useHttpClient } from '@bonusly/http-client';
import {
	CalendarEventAttendeeEntity,
	CalendarEventEntity,
	CalendarEventStatus,
	CalendarEventType,
	EventEntity,
	EventFrequency,
	EventOccurrence,
	MeetingTimelineStatus,
} from '@bonusly/one-on-ones-entities';
import { assertPresence } from '@bonusly/runtime';
import { Schema } from '@bonusly/schema';

import { QueryKey as GrowthQueryKey } from '../modules/growth/types/growth-query-key';

export interface EventAPIData {
	id: string;
	canceled_at: string;
	company_id: string;
	created_at: string;
	end_time: string | null;
	external_calendar_id?: string | null;
	external_calendar_link?: string | null;
	external_calendar_provider?: string | null;
	frequency: EventFrequency;
	next_meeting_date: string | null;
	schedule_day_of_week: string;
	schedule_occurrence_in_month: EventOccurrence | null;
	shortcoded_url: string;
	start_time: string | null;
	guest_id: string;
	host_id: string;
	manager_id: string | null;
	skip_manager_id: boolean;
	updated_at: string;
	transcription?: any | null;
}

export interface CreateEventAPIData {
	// legacy time fields - kept for backwards compatibility
	end_time?: string | null;
	start_time?: string | null;
	// external calendar fields - optional
	external_calendar_id?: string | null;
	external_calendar_link?: string | null;
	external_calendar_provider?: string | null;
	// required fields
	frequency: string;
	guest_id: string;
	host_id: string;
	schedule_day_of_week: string;
	schedule_occurrence_in_month: string | null;
	// schedule fields - required for consistent event creation
	schedule_date: string;
	schedule_duration: number;
	schedule_time_of_day: string;
	schedule_time_zone: string;
}

export interface UpdateEventAPIData {
	end_time?: string | null;
	external_calendar_id?: string | null;
	external_calendar_link?: string | null;
	external_calendar_provider?: string | null;
	frequency?: string;
	meeting_date?: string | null;
	schedule_day_of_week?: string;
	schedule_duration?: number;
	schedule_occurrence_in_month?: string | null;
	schedule_time_of_day?: string;
	schedule_time_zone?: string;
	start_time?: string | null;
}

export interface MeetingDetailAPIData {
	id: string;
	company_id: string;
	end_time: string | null;
	event_id: string;
	host_id: string;
	guest_id: string;
	status: string;
	timeline_status: MeetingTimelineStatus;
	week_number: number;
	version_flag: number;
	start_time: string | null;
	started_at: string;
	created_at: string;
	updated_at: string;
	event: EventAPIData;
}

export interface CalendarEventTimeField {
	dateTime: string;
	timeZone: string;
}

export interface CalendarEventAPIData {
	id: string;
	attendees: CalendarEventAttendeeEntity[] | null;
	end: CalendarEventTimeField;
	eventType: CalendarEventType;
	htmlLink: string;
	start: CalendarEventTimeField;
	status: CalendarEventStatus;
	summary: string;
	recurrence: string[] | null;
	recurringEventId: string | null;
}

export interface CalendarEventsAPIData {
	items: CalendarEventAPIData[];
}

type IntegrationsCalendarsLinkAccountAPIData = {
	eventId: string | null;
	redirectUrl: string;
	path: string;
	guestId: string;
	meetingId: string | null;
	calendarProvider: string;
};

type IntegrationsCalendarsLinkProps = {
	calendarProvider: string | null;
	eventId?: string | null;
	guestId: string;
	meetingId?: string;
	redirectPath: string;
};

enum QueryKey {
	CalendarIntegrations = 'one-on-ones_integrations_calendars',
	Events = 'one-on-ones_events',
	Meetings = 'one-on-ones_meetings',
}

export const useIntegrationsCalendarsLink = ({ calendarProvider, eventId = null, guestId, meetingId, redirectPath }: IntegrationsCalendarsLinkProps) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!calendarProvider && !!guestId,
		queryKey: [QueryKey.CalendarIntegrations, 'link_account', calendarProvider, guestId, redirectPath],
		queryFn: async () => {
			const res = await http.post<IntegrationsCalendarsLinkAccountAPIData>(
				'/growth/integrations/calendars/link_account',
				{
					calendarProvider,
					eventId,
					guestId,
					meetingId,
					path: redirectPath,
				},
				{ headers: { 'Key-Inflection': 'camel' } }
			);
			return res.data;
		},
		staleTime: 0,
	});
};

type useIntegrationsCalendarsProps = {
	bonuslyCreated?: boolean;
	calendarProvider: string;
	participantEmail: string;
};

export const useIntegrationsCalendars = ({ calendarProvider, participantEmail, bonuslyCreated }: useIntegrationsCalendarsProps) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!calendarProvider && !!participantEmail,
		queryKey: [QueryKey.CalendarIntegrations, calendarProvider, participantEmail],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.set('bonusly_created', String(bonuslyCreated || false));
			params.set('calendar_provider', calendarProvider);
			params.set('with', participantEmail);

			const url = `/growth/integrations/calendars?${params.toString()}`;
			const res = await http.get<CalendarEventsAPIData>(url, { headers: { 'Key-Inflection': 'camel' } });
			return Schema.deserialize(CalendarEventEntity, res.data.items);
		},
		refetchOnWindowFocus: true,
		staleTime: 0,
	});
};

export const eventQueryKey = (id: string) => [QueryKey.Events, id];
export const eventsQueryKey = (params: Record<string, any> = {}) => [QueryKey.Events, params];

// NOTE: Similar to query keys, the API paths are declared here and accessible via the helpers below.
// Devs problably will never use these in production, but the `prefix` argument exists to allow for passing in `"*"` for testing purposes.
export const eventsPath = (prefix = '') => `${prefix}/one_on_ones/events`;
export const eventPath = (id: string, prefix = '') => `${prefix}/one_on_ones/events/${id}`;
export const eventCancelPath = (id: string, prefix = '') => eventPath(id, prefix) + '/cancel';

// NOTE: Invalidations were one of the things you had to know the query key for in order to invalidate a query.
// That is no more! Just import the hook and you're good to go.
export const useInvalidateEvent = () => {
	const client = useQueryClient();

	return useCallback(
		(id: string) => {
			client.invalidateQueries(eventQueryKey(id));
		},
		[client]
	);
};

export const useInvalidateEvents = () => {
	const client = useQueryClient();

	return useCallback(
		(params: Record<string, any> = {}) => {
			client.invalidateQueries(eventsQueryKey(params));
			// also invalidate the growth module's user events query
			client.invalidateQueries({ queryKey: [GrowthQueryKey.UserEvents] });
		},
		[client]
	);
};

export const useEvent = (id?: string) => {
	const http = useHttpClient();

	return useQuery({
		enabled: !!id,
		queryKey: [QueryKey.Events, id],
		queryFn: async () => {
			assertPresence(id);
			const res = await http.get<EventAPIData>(eventPath(id));
			return Schema.deserialize(EventEntity, res.data);
		},
	});
};

export type MutateEventOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (event: EventEntity) => void;
};

// NOTE: Below you also have your mutation hooks, which take some additional options for handling errors and successes.
export const useCreateEvent = (options: MutateEventOptions = {}) => {
	const http = useHttpClient();
	const invalidateEvents = useInvalidateEvents();

	return useMutation({
		mutationFn: async (data: CreateEventAPIData, mutateOptions: MutateEventOptions = {}) => {
			const allOptions = { ...options, ...mutateOptions };

			try {
				const res = await http.post<EventAPIData>(eventsPath(), data);
				const event = Schema.deserialize(EventEntity, res.data);

				invalidateEvents();
				allOptions.onSuccess?.(event);

				return event;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

// NOTE: The goal of these mutations are mostly to allow the user to change data without having to know all of the details of what needs to be updated
// in the query cache after the data is changed - all of that is centralized here. While these can serve as a starting point for how to create a hook,
// the things that have to happen after a successful request are going to be dependent on the specific use case.
export const useUpdateEvent = <T extends { id: string }>(options: MutateEventOptions = {}) => {
	const http = useHttpClient();
	const invalidateEvent = useInvalidateEvent();
	const invalidateEvents = useInvalidateEvents();

	return useMutation({
		mutationFn: async (
			data: {
				eventOrId: string | T;
				data: UpdateEventAPIData;
			},
			mutateOptions: MutateEventOptions = {}
		) => {
			const allOptions = { ...options, ...mutateOptions };

			try {
				const id = typeof data.eventOrId === 'string' ? data.eventOrId : data.eventOrId.id;

				const res = await http.patch<EventAPIData>(eventPath(id), data.data);

				const event = Schema.deserialize(EventEntity, res.data);

				invalidateEvents();
				invalidateEvent(event.id);
				allOptions.onSuccess?.(event);

				return event;
			} catch (e) {
				allOptions.onError?.(e);
				throw e;
			}
		},
	});
};

type CancelEventOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: () => void;
};

export const useCancelEvent = <T extends { id: string }>(options: CancelEventOptions = {}) => {
	const http = useHttpClient();
	const invalidateEvents = useInvalidateEvents();

	return useMutation({
		mutationFn: async (eventOrId: string | T, mutateOptions: CancelEventOptions = {}) => {
			const allOptions = { ...options, ...mutateOptions };

			try {
				const id = typeof eventOrId === 'string' ? eventOrId : eventOrId.id;
				await http.patch<EventAPIData>(eventCancelPath(id));
				invalidateEvents();
				allOptions.onSuccess?.();
			} catch (e) {
				allOptions.onError?.(e);
				throw e;
			}
		},
	});
};

export const meetingQueryKey = (id: string) => [QueryKey.Meetings, id];
export const meetingsQueryKey = (params: Record<string, any> = {}) => [QueryKey.Meetings, params];

export const useInvalidateMeeting = () => {
	const client = useQueryClient();

	return useCallback(
		(id: string) => {
			client.invalidateQueries(meetingQueryKey(id));
		},
		[client]
	);
};

export const useInvalidateMeetings = () => {
	const client = useQueryClient();

	return useCallback(
		(params: Record<string, any> = {}) => {
			client.invalidateQueries(meetingsQueryKey(params));
		},
		[client]
	);
};
